/**
 * The client portal — Phase 11 (decisions D146–D149).
 *
 * ===========================================================================
 * PORTAL LEAKAGE IS THIS PHASE'S HIGHEST-RATED RISK. READ THIS FIRST.
 * ===========================================================================
 *
 * The mitigation is structural, and this file holds its shared half:
 *
 *  1. A SEPARATE AUTHORIZATION CONTEXT (D147). A portal access token has its
 *     own type (`PORTAL_ACCESS_TOKEN_TYPE`) and its own audience
 *     (`PORTAL_TOKEN_AUDIENCE`), so a token minted for one context is not
 *     merely unauthorised in the other but unrecognised. A staff token is
 *     refused on every portal route, and a portal token on every staff route.
 *
 *  2. AN ALLOW-LIST PER RECORD TYPE, AND NOTHING ELSE (D148). Each
 *     `PORTAL_*_FIELDS` list below is the COMPLETE set of keys a portal
 *     response of that kind may carry, frozen, and the leakage suite compares
 *     every real response against it key for key. Adding a key here is adding
 *     something every client of every laboratory will see: it needs a
 *     decision, not a convenience.
 *
 *  3. NO MONEY ON A REQUEST OR A REPORT. Prices reach the portal in exactly
 *     one place — invoices and the statement — gated by one check (financial
 *     visibility, which is always an explicit grant).
 *
 *  4. NOTHING INTERNAL. No note, internal note, reason, override reason,
 *     remark, result text, person, file id, storage key, hash or token. No
 *     draft or discarded invoice. No unreleased report version. A superseded
 *     version is never presented as current.
 *
 * Portal sign-ins, sign-outs, password changes and downloads are recorded as
 * `portal_access_events` (append-only), not in the audit log: an audit entry
 * means a LABORATORY MEMBER acted.
 */
import { PORTAL_VISIBILITY } from './commercial.js';
// ---------------------------------------------------------------------------
// The portal's own tokens (D147)
// ---------------------------------------------------------------------------
/** The `typ` claim of a portal access token. The staff verifier refuses it. */
export const PORTAL_ACCESS_TOKEN_TYPE = 'lims-portal-access';
/** The `aud` claim of a portal access token. The staff verifier refuses it. */
export const PORTAL_TOKEN_AUDIENCE = 'lims-portal';
// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------
/**
 * Which of its client's projects an account sees (D146). Work orders and
 * reports are scoped by project; a work order WITHOUT a project is visible only
 * under `all_projects`. Invoices and the statement are client-wide.
 */
export const PORTAL_PROJECT_SCOPE = {
    ALL_PROJECTS: 'all_projects',
    SELECTED_PROJECTS: 'selected_projects',
};
export const ALL_PORTAL_PROJECT_SCOPES = Object.freeze(Object.values(PORTAL_PROJECT_SCOPE));
/**
 * The visibilities a laboratory may choose as its DEFAULT for a contact
 * enabled for the portal (`tenant_settings.portal_default_visibility`). `none`
 * is not among them: a contact enabled for the portal sees at least reports.
 * The default itself lives only in the backend (CLAUDE.md section 6.1).
 */
export const PORTAL_DEFAULT_VISIBILITIES = Object.freeze([
    PORTAL_VISIBILITY.REPORTS,
    PORTAL_VISIBILITY.REPORTS_AND_INVOICES,
]);
/** Does this visibility include invoices and the statement? */
export function portalVisibilityIsFinancial(visibility) {
    return visibility === PORTAL_VISIBILITY.REPORTS_AND_INVOICES;
}
/**
 * What a portal account did (D147) — append-only `portal_access_events`.
 * Failed sign-ins are NOT here: no tenant is known for them, and they go to
 * `login_attempts` as a staff failure does.
 */
export const PORTAL_ACCESS_EVENT_KIND = {
    SIGN_IN: 'sign_in',
    SIGN_OUT: 'sign_out',
    PASSWORD_RESET_COMPLETED: 'password_reset_completed',
    /** A download is evidence that somebody signed in as the contact fetched the
     *  file. It is NOT a delivery and NOT a receipt (D149). */
    REPORT_FILE_DOWNLOADED: 'report_file_downloaded',
    REPORT_FILE_REFUSED: 'report_file_refused',
};
export const ALL_PORTAL_ACCESS_EVENT_KINDS = Object.freeze(Object.values(PORTAL_ACCESS_EVENT_KIND));
/** A recorded act on one report version (D149) — append-only
 *  `report_portal_releases`. A version is IN the portal iff its latest action
 *  is `released`. */
export const PORTAL_RELEASE_ACTION = {
    RELEASED: 'released',
    WITHDRAWN: 'withdrawn',
};
export const ALL_PORTAL_RELEASE_ACTIONS = Object.freeze(Object.values(PORTAL_RELEASE_ACTION));
/** Where a report stands in the portal, for the staff report page. */
export const PORTAL_RELEASE_STATE = {
    NOT_RELEASED: 'not_released',
    RELEASED: 'released',
    WITHDRAWN: 'withdrawn',
};
export const ALL_PORTAL_RELEASE_STATES = Object.freeze(Object.values(PORTAL_RELEASE_STATE));
// ---------------------------------------------------------------------------
// THE ALLOW-LISTS (D148). Every portal response body's key set is EXACTLY one
// of these. Nested objects have their own lists where they carry more than an
// id and a name.
// ---------------------------------------------------------------------------
/** `GET /portal/me`. */
export const PORTAL_ME_FIELDS = Object.freeze([
    'id',
    'displayName',
    'email',
    'preferredLocale',
    'client',
    'laboratory',
    'financialVisibility',
    'projectScope',
    'projects',
]);
/** One row of `GET /portal/work-orders`. No price, discount, total, note,
 *  sample, assignment, person, department or branch. */
export const PORTAL_WORK_ORDER_FIELDS = Object.freeze([
    'id',
    'number',
    'subject',
    'project',
    'purchaseOrderNumber',
    'clientReference',
    'requestDate',
    'dueDate',
    'status',
    'progress',
    'cancelled',
]);
/** `GET /portal/work-orders/{id}`: the row, its services, and the ids of the
 *  reports THIS account may see. */
export const PORTAL_WORK_ORDER_DETAIL_FIELDS = Object.freeze([
    ...PORTAL_WORK_ORDER_FIELDS,
    'services',
    'reportIds',
]);
/** One requested service on a portal work order — never a price. */
export const PORTAL_WORK_ORDER_SERVICE_FIELDS = Object.freeze([
    'description',
    'quantity',
    'state',
]);
/** One report of `GET /portal/reports` and `GET /portal/reports/{id}`. Never
 *  a remark, a result, a person, the hold or its reason, the verification
 *  token, a file id or a hash. */
export const PORTAL_REPORT_FIELDS = Object.freeze([
    'id',
    'reportNumber',
    'workOrder',
    'project',
    'versions',
    'current',
    'newerVersionNotice',
]);
/** One RELEASED version inside a portal report. `status` is that version's
 *  CURRENT status, so a superseded version says so. */
export const PORTAL_REPORT_VERSION_FIELDS = Object.freeze([
    'versionNumber',
    'status',
    'issuedAt',
    'pageCount',
    'language',
    'testTitles',
    'releasedAt',
    'fileAvailable',
]);
/** One row of `GET /portal/invoices` — issued and cancelled only. */
export const PORTAL_INVOICE_FIELDS = Object.freeze([
    'id',
    'invoiceNumber',
    'status',
    'issueDate',
    'dueDate',
    'currency',
    'totalMinor',
    'paymentStatus',
    'balanceMinor',
    'overdue',
]);
/** `GET /portal/invoices/{id}`. Never `internalNote`, `priceOverrideReason`,
 *  draft data, a person, a payment's note or an adjustment's reason. */
export const PORTAL_INVOICE_DETAIL_FIELDS = Object.freeze([
    ...PORTAL_INVOICE_FIELDS,
    'taxRatePercent',
    'totals',
    'lines',
    'clientNote',
    'laboratory',
    'client',
    'payments',
    'adjustments',
]);
/** One line of a portal invoice. */
export const PORTAL_INVOICE_LINE_FIELDS = Object.freeze([
    'lineNumber',
    'description',
    'quantity',
    'unitPriceMinor',
    'discount',
    'subtotalMinor',
    'discountMinor',
    'totalMinor',
    'workOrderNumber',
]);
/** One payment applied to a portal invoice: the ACTIVE allocation's amount,
 *  with the payment's date, method and reference. No note. */
export const PORTAL_INVOICE_PAYMENT_FIELDS = Object.freeze([
    'receivedOn',
    'method',
    'reference',
    'amountMinor',
]);
/** One adjustment on a portal invoice — WITHOUT its reason. */
export const PORTAL_INVOICE_ADJUSTMENT_FIELDS = Object.freeze([
    'kind',
    'amountMinor',
    'effectiveOn',
]);
/** `GET /portal/statement` — the SAME computation as the staff statement. */
export const PORTAL_STATEMENT_FIELDS = Object.freeze([
    'currency',
    'from',
    'to',
    'asOf',
    'defaultsApplied',
    'openingBalanceMinor',
    'rows',
    'closingBalanceMinor',
    'totals',
]);
/** One row of the portal statement. No note, no reason, no internal id. */
export const PORTAL_STATEMENT_ENTRY_FIELDS = Object.freeze([
    'entryDate',
    'kind',
    'documentNumber',
    'debitMinor',
    'creditMinor',
    'balanceMinor',
]);
/**
 * Copy exactly the allow-listed keys of `source`, in the list's order. A key
 * missing from `source` is copied as `null` rather than left out, so the key
 * set of every answer is the list itself.
 */
export function pickPortalFields(source, fields) {
    const picked = {};
    for (const field of fields) {
        picked[field] = Object.prototype.hasOwnProperty.call(source, field)
            ? source[field]
            : null;
    }
    return picked;
}
//# sourceMappingURL=portal.js.map